import CloseIcon from '@mui/icons-material/Close'
import { Box, Divider, IconButton, Link, Stack, Typography } from '@mui/material'
import { ButtonActions } from 'components-ui/src/button_actions/button_actions'
import { SimpleModal } from 'components-ui/src/simple_modal/simple_modal'
import type { CustomInvoiceClientDto } from 'service-api/src/types/api'
import { i18WithParams as t } from 'shared/locale'
import { ServiceTypeAvatar } from 'components-ui/src/icon_avatar/icon_avatar'
import { datesFormated, petsName } from '@components/double_booking/helper'
import { useRoutes } from '../../app/use_routes'

type Props = {
  invoices: CustomInvoiceClientDto[]
  locationPetProfileIds: string[]
  onClose: () => void
  onBookAnyway: () => Promise<void> | void
  onReviewDates: () => void
}

export const BookingConflictModalContent = ({
  invoices,
  onClose,
  onBookAnyway,
  locationPetProfileIds,
  onReviewDates,
}: Props) => {
  const { buildPath } = useRoutes()
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h5">{t('petConflictModal.title')}</Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 520 }}>
            {t('petConflictModal.subTitle')}
          </Typography>
        </Box>

        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ mt: 4 }}>
        <Stack
          divider={<Divider />}
          spacing={0}
          sx={{
            maxHeight: '50vh',
            overflowY: 'auto',
          }}
        >
          {invoices.map((invoice) => (
            <Stack
              key={invoice.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                py: 3,
                maxHeight: 400,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                <Typography variant="body1bold" sx={{ minWidth: 140 }}>
                  {petsName(invoice.petRelations, locationPetProfileIds)}
                </Typography>

                <ServiceTypeAvatar
                  serviceType={invoice.locationServiceType?.serviceType?.name}
                  size="small"
                  fontSize="small"
                />

                <Box sx={{ ml: 1 }}>
                  {datesFormated(invoice.period?.[0], invoice.locationServiceType?.serviceType?.overnight)}
                </Box>
              </Stack>

              {invoice.orderId && (
                <Link
                  target="_blank"
                  href={buildPath('purchaseSummary', {
                    bookingId: invoice.orderId,
                  })}
                >
                  {t('global.label.view')}
                </Link>
              )}
            </Stack>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ mt: 2 }} />

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
        sx={{
          pt: 3,
        }}
      >
        <Stack direction="row" justifyContent="flex-end">
          <ButtonActions
            actionVariant="secondary"
            onAction={async () => onReviewDates()}
            onActionLabel={t('petConflictModal.reviewDates')}
            onCancel={async () => onBookAnyway()}
            onCancelLabel={t('petConflictModal.bookAnyway')}
          />
        </Stack>
      </Stack>
    </Box>
  )
}

export const BookingConflictModal = ({
  isOpen,
  onClose,
  invoices,
  locationPetProfileIds,
  onBookAnyway,
  onReviewDates,
}: Props & {
  isOpen: boolean
}) => (
  <SimpleModal open={isOpen} onClose={onClose} sx={{ width: 600, p: 3, maxHeight: '90vh', overflowY: 'auto' }}>
    <BookingConflictModalContent
      invoices={invoices}
      locationPetProfileIds={locationPetProfileIds}
      onClose={onClose}
      onBookAnyway={onBookAnyway}
      onReviewDates={onReviewDates}
    />
  </SimpleModal>
)
